<template>
  <div class="create-claim cc">
    <div v-if="!hasPermission('CanSubmitDiscrepancyClaims')" class="container">
      <b-row>
        <b-col>
          <not-authorized />
        </b-col>
      </b-row>
    </div>
    <div v-else>
      <div v-show="isClaimsBlackedOut" class="cc__blackout-div">
        <h5>{{ ClaimBlackoutMessage }}</h5>
      </div>
      <div v-show="!isLoading && !isClaimsBlackedOut" id="create-claim" class="container create-claim cc">
        <div class="create-claim__col">
          <div class="create-claim__row">
            <h1 class="create-claim__title">New Claim</h1>
          </div>

          <div class="create-claim__row">
            <b-alert v-show="claimAllowedDaysMessage !== ''" id="claim-allowed-days-message" variant="secondary" show>
              {{ claimAllowedDaysMessage }}
            </b-alert>
          </div>
        </div>
        <!-- Claim Type Selection -->
        <div
          v-show="claimDto.ClaimType === null"
          id="claim-type"
          class="create-claim__row create-claim__row--no-gutters"
        >
          <h3 class="create-claim__subtitle">
            Select Claim Type&nbsp;<span class="create-claim__required">*&nbsp;&nbsp;</span>
          </h3>
        </div>

        <b-row v-show="claimDto.ClaimType === null" class="create-claim__options-row">
          <b-col v-for="type in ClaimTypeOptions" :key="type.value" class="create-claim__options-col" xs="12" lg="4">
            <div class="create-claim__option" @click="SetClaimType(type.value)">
              <button class="create-claim__option-button" @click="SetClaimType(type.value)">
                {{ type.text }}
              </button>
              <p class="create-claim__option-text">{{ type.description }}</p>
            </div>
          </b-col>
        </b-row>
        <!-- Show claim stepper when not at submission stage -->
        <div v-show="ClaimStatusEnumeration === 0">
          <claim-stepper
            ref="stepper"
            :initial-active-tab-id="currentTabId"
            :claim-dto="claimDto"
            @ResetClaimStatusEnumeration="ResetClaimSubmissionStatus"
            @ReviewSubmit="ShowClaimSubmission"
          />
        </div>
        <!-- Show when submitting claim -->
        <div v-show="ClaimStatusEnumeration > 0">
          <claim-submission
            id="claim-submission-dialog"
            :claim-status-enumeration="ClaimStatusEnumeration"
            @ConfirmClaim="ConfirmClaim()"
            @GoToClaimsIndex="GoToClaimsIndex"
            @ResetClaimSubmissionStatus="ResetClaimSubmissionStatus"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import { mapGetters } from "vuex";

// components
import CreateClaimOrderLookup from "./CreateClaimOrderLookup";
import CreateClaimSelectParts from "./CreateClaimSelectParts";
import CreateClaimAddParts from "./CreateClaimAddParts";
import CreateClaimClaimInfo from "./CreateClaimClaimInfo";
import CreateClaimFreightClaimInfo from "./CreateClaimFreightClaimInfo";
import CreateClaimContactInfo from "./CreateClaimContactInfo";
import CreateClaimReviewAndSubmit from "./CreateClaimReviewAndSubmit";
import ClaimSubmission from "./ClaimSubmission";
import ClaimStepper from "./ClaimStepper";

// services
import { ClaimsService } from "@/services/ClaimsService";
import { ReturnsService } from "@/services/ReturnsService";

// mixins
import { formatDateTime, stringHelper, sizeHelper, siteSectionsHelper } from "@/components/mixins";

// elements
import DnStepper from "@/components/Elements/DnStepper";
import { constants } from "os";
import NotAuthorized from "@/components/Elements/NotAuthorized";
import ToolTipPopOver from "@/components/Elements/ToolTipPopover";

export default {
  components: {
    NotAuthorized,
    ClaimSubmission,
    ClaimStepper,
  },
  mixins: [stringHelper, sizeHelper, siteSectionsHelper, formatDateTime],
  data: () => ({
    errorMessage: "",
    //0 not submitting yet, 1 pre-submit, 2 submit-waiting, 3 successful, 4 fail/error
    ClaimStatusEnumeration: 0,
    claimAllowedDaysMessage: "",
    currentTabId: "orderLookupTab",
    isClaimsBlackedOut: null,
    claimsBlackoutStartDate: null,
    claimsBlackoutEndDate: null,
    isLoading: true,
    claimDto: {
      CustomerReferenceNumber: "",
      Source: String,
      InvoiceSource: String,
      InvoiceDate: String,
      InvoiceNumber: String,
      InvoiceFreight: null,
      DealerCode: String,
      OrderNumber: "",
      Items: Array,
      ContactName: String(),
      ContactPhone: String(),
      ContactPhoneExtension: String(),
      ContactEmail: String(),
      ContactFax: String(),
      SalesOrganization: String,
      ClaimType: null,
      FreightExpectedCredit: 0.0,
      FreightComment: "",
      ClaimImagesBase64: [],
      ClaimImagesFilenames: [],
      SourceInvoiceNumber: "",
      SourceCode: "",
    },
    claimPlacementResultDto: Array,
    invoiceData: {
      invoiceId: Number,
      invoiceSource: String,
    },
    claimPopupMessage: `
      <b>Parts Claim</b> – Standard claim options
      <br/>
      <b>Freight Only Claim</b> – Claim is for freight reimbursement <i>only</i>
      <br/>
      <b>Received Parts Not Ordered</b> – Parts received that are <i>not</i> on packing slip
      `,
  }),

  computed: {
    ...mapGetters([
      "activeAccountId",
      "user",
      "hasPermission",
      "getRecentComponentData",
      "McfCostCenterId",
      "getContentItem",
    ]),
    HasErrors: function () {
      return this.errorMessage.length > 0;
    },
    ClaimTypeOptions: function () {
      var claimTypeOptions = [
        { value: "PartsIssue", text: "Parts Claim", description: this.getContentItem("PartsClaimDescription") },
      ];
      if (this.IsClaimsFreightSectionEnabled) {
        claimTypeOptions.push({
          value: "FreightOnly",
          text: "Freight Only Claim",
          description: this.getContentItem("FreightOnlyClaimDescription"),
        });
      }
      if (this.IsClaimsExtraPartsSectionEnabled) {
        claimTypeOptions.push({
          value: "ExtraParts",
          text: "Received Parts Not Ordered",
          description: this.getContentItem("ReceivedPartsNotOrderedDescription"),
        });
      }
      return claimTypeOptions;
    },
    ClaimBlackoutMessage() {
      return this.format(this.getContentItem("ClaimsBlackoutMessage"), this.getLocalDate(this.claimsBlackoutEndDate));
    },
  },

  created() {
    if (this.$route.query.fromInvoice) {
      this.PopulateClaimFromInvoice();
    }
    this.LoadClaimConfiguration();
  },

  mounted() {
    this.SetContactInfo();
    if (!this.IsClaimsFreightSectionEnabled && !this.IsClaimsExtraPartsSectionEnabled) {
      this.claimDto.ClaimType = "PartsIssue";
    }

    if (this.$route.query.fromInvoice) {
      this.$refs.stepper.GetCurrentStepperClaimInfoComponent().Setup();
    }
  },

  methods: {
    GoToClaimsIndex(bvModalEvt) {
      this.$router.push("/claimsAndReturns/claims");
    },
    ConfirmClaim() {
      this.ClaimStatusEnumeration = 2;
      var claimResult = this.SubmitClaimDto(this.claimDto).then((claimResponse) => {
        let responseDto = [];
        let responseErrors = false;
        for (let i = 0; i < claimResponse.data.length; i++) {
          if (!claimResponse.data[i].Success) {
            responseErrors = true;
          }
          responseDto.push({
            ClaimReferenceNumber: claimResponse.data[i].ClaimReferenceNumber,
            Errors: claimResponse.data[i].Errors,
            Success: claimResponse.data[i].Success,
          });
        }
        this.claimPlacementResultDto = responseDto;
        if (responseErrors) {
          this.ClaimStatusEnumeration = 4;
        } else {
          this.ClaimStatusEnumeration = 3;
        }
      });
    },
    ResetClaimSubmissionStatus() {
      this.ClaimStatusEnumeration = 0;
    },
    SubmitClaimDto(claimDto) {
      return ClaimsService.SubmitClaim(this.activeAccountId, this.user.UserId, claimDto);
    },
    PopulateClaimFromInvoice() {
      let claimData = this.getRecentComponentData.claimData;
      if (claimData) {
        this.claimDto = claimData;
        this.SetContactInfo();
      }

      let invoiceData = this.getRecentComponentData.invoiceData;
      if (invoiceData) {
        this.invoiceData = invoiceData;
      }

      let currentTabId = this.getRecentComponentData.currentTabId;
      if (currentTabId) {
        this.currentTabId = currentTabId;
      }
    },
    SetClaimType(claimType) {
      this.claimDto.Items = [];
      this.claimDto.ClaimType = claimType;
    },
    SetContactInfo() {
      this.claimDto.ContactName = this.user.FullName;
      this.claimDto.ContactPhone = this.user.Phone;
      this.claimDto.ContactPhoneExtension = this.user.Extension;
      this.claimDto.ContactEmail = this.user.Email;
      this.claimDto.ContactFax = this.user.Fax;
      this.claimDto.DealerCode = this.user.DealerCode;
    },
    SetPartDetailsForItems() {
      if (this.claimDto.Items.length === 0) {
        return;
      }

      let itemPartCodes = this.claimDto.Items.map((x) => x.ItemNumber);
    },
    ShowClaimSubmission() {
      this.ClaimStatusEnumeration = 1;
    },
    async LoadClaimConfiguration() {
      //load claim allowed days
      var claimAllowedDays = await ClaimsService.GetClaimAllowedDays(this.McfCostCenterId);
      this.claimAllowedDaysMessage = `Claims are only valid ${claimAllowedDays} days from the invoice date.`;

      //load blackout status
      ReturnsService.GetBlackoutStatus(this.McfCostCenterId).then((blackoutStatus) => {
        this.isClaimsBlackedOut = blackoutStatus.data.ClaimsBlackedOut;
        this.claimsBlackoutStartDate = blackoutStatus.data.ClaimsBlackoutStartDate;
        this.claimsBlackoutEndDate = blackoutStatus.data.ClaimsBlackoutEndDate;
        this.isLoading = false;
      });
    },
  },
};
</script>

<style lang="scss">
.create-claim__options-row {
  padding: 24px 6px;
}
.create-claim__options-col {
  padding: 5px;
}
.create-claim__option-button {
  display: block;
  margin-bottom: 10px;
  width: 100%;
  height: 52px;
  //DN blue
  background-color: $brand;
  border: none;
  font-size: 1.2em;
  color: white;
  font-weight: 500;
}

.create-claim__option-text {
  margin: 10px 1rem;
  text-align: center;
}

.create-claim__option {
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid gray;
  min-height: 100%;
  border-radius: 5px;
  margin-top: 10px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
  &:hover {
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.7);
    cursor: pointer;
  }
}

.create-claim__title {
  margin-bottom: 1rem;
}

.cc__blackout-div {
  margin-left: 8%;
  padding: 10px;
  background-color: #f8f9fa !important;
  width: 84%;
  padding: 2rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  margin: 1% 0% 2% 8%;
}

.cc {
  min-height: 180px;
}
</style>